import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import { Card, CardContent } from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/karuna/components/ui/select"; // @ts-ignore - select is not a valid module
import { Search, Eye, Calendar, School, Loader2, FileText } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { DynamicModuleService } from "@/services/APIs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { removeMetaFields } from "@/lib/removeMetaFields";
import {
  KarunaPagination,
  ListCardViewToggle,
  type KarunaViewMode,
} from "@/project/karuna/components/shared/KarunaListControls";
import { useKarunaPagedList } from "@/project/karuna/hooks/useKarunaPagedList";
import {
  mapWithConcurrency,
  DEFAULT_API_FETCH_CONCURRENCY,
} from "@/project/karuna/utils/mapWithConcurrency";

const SUBMISSION_SCHEMA_NAME = "Activity Submission";
const SCHOOL_PROFILE_SCHEMA_NAME = "School Profile";
const ACTIVITY_SCHEMA_NAME = "Activity Management";

interface Submission {
  id: string;
  event_id: string;
  status: string;
  score: number | null;
  submitted_at: string;
  created_at: string;
  school: {
    school_name: string;
    kc_no: string;
    kendra_name: string;
  };
  activity: {
    title: string;
  };
  teacher: {
    name: string;
  } | null;
}

// Normalize mobile number helper
const normalizeMobileNumber = (mobileNumber: string): string => {
  return String(mobileNumber ?? "")
    .replace(/\D/g, "")
    .slice(0, 10);
};

// Program name mapping (from schema enum values)
const programNameMap: Record<string, string> = {
  intraschool: "Organising Intra School Programmes",
  interschool: "Organising Inter School Programmes",
  participationinter: "Participation in Inter-School Competitions/Programmes",
  publicrelation: "Public Relation Programmes",
  karunaclub: "Karuna Club – Internal / External Growth",
  nationallevel: "Organising / Participation in National Level Programme",
};

const AdminSubmissions = () => {
  const navigate = useNavigate();
  const getLink = useKarunaLink();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<KarunaViewMode>("card");

  // Fetch all submissions from Activity Submission schema
  const { data: submissionsData, isLoading: submissionsLoading } = useQuery({
    queryKey: ["admin-submissions", SUBMISSION_SCHEMA_NAME],
    queryFn: async () => {
      try {
        const allSubmissions =
          await DynamicModuleService.getGridColItemsAllPages(
            SUBMISSION_SCHEMA_NAME
          );

        const submissionsWithFullData = await mapWithConcurrency(
          allSubmissions,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (submission: any) => {
            const submissionId = submission.id ?? submission._id;
            if (!submissionId) {
              return submission;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                SUBMISSION_SCHEMA_NAME,
                submissionId
              );
              const data = fullData?.data ?? fullData ?? submission;
              return { ...submission, ...data };
            } catch (err) {
              console.warn(
                `Could not fetch full data for submission ${submissionId}:`,
                err
              );
              return submission;
            }
          }
        );

        return submissionsWithFullData;
      } catch (error) {
        console.error("Error fetching submissions:", error);
        toast({
          title: "Error",
          description: "Failed to load submissions. Please try again.",
          variant: "destructive",
        });
        return [];
      }
    },
    refetchOnWindowFocus: true,
  });

  // Fetch activities to map event_id to activity title
  const { data: activitiesData } = useQuery({
    queryKey: ["activities", ACTIVITY_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await DynamicModuleService.getGridColItemsAllPages(
          ACTIVITY_SCHEMA_NAME
        );
      } catch (error) {
        console.error("Error fetching activities:", error);
        return [];
      }
    },
  });

  // Fetch school profiles to get school details by contact
  const { data: schoolProfilesData } = useQuery({
    queryKey: ["school-profiles", SCHOOL_PROFILE_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await DynamicModuleService.getGridColItemsAllPages(
          SCHOOL_PROFILE_SCHEMA_NAME
        );
      } catch (error) {
        console.error("Error fetching school profiles:", error);
        return [];
      }
    },
  });

  // Create a map of activity ID to title
  const activityMap = useMemo(() => {
    if (!activitiesData || !Array.isArray(activitiesData)) {
      return new Map<string, string>();
    }
    const map = new Map<string, string>();
    activitiesData.forEach((activity: any) => {
      const activityId = String(activity.id ?? activity._id ?? "").trim();
      const title =
        activity.title ??
        activity.activitytitle ??
        activity.activity_title ??
        "";
      if (activityId && title) {
        map.set(activityId, title);
      }
    });
    return map;
  }, [activitiesData]);

  // Create a map of contact number to school profile
  const schoolProfileMap = useMemo(() => {
    if (!schoolProfilesData || !Array.isArray(schoolProfilesData)) {
      return new Map<string, any>();
    }
    const map = new Map<string, any>();
    schoolProfilesData.forEach((profile: any) => {
      const contact = normalizeMobileNumber(
        profile.contact ?? profile.school_contact ?? profile.schoolcontact ?? ""
      );
      if (contact) {
        map.set(contact, profile);
      }
    });
    return map;
  }, [schoolProfilesData]);

  // Transform submissions data
  const submissions = useMemo((): Submission[] => {
    if (!submissionsData || !Array.isArray(submissionsData)) {
      return [];
    }

    return submissionsData
      .map((submission: any): Submission | null => {
        const submissionId = submission.id ?? submission._id ?? "";
        if (!submissionId) {
          return null;
        }

        // Get contact number from submission
        const submissionContact = normalizeMobileNumber(
          submission.schoolcontact ??
            submission.contact ??
            submission.school_contact ??
            ""
        );

        // Get school profile by contact
        const schoolProfile = submissionContact
          ? schoolProfileMap.get(submissionContact)
          : null;

        // Get program name
        const programValue = submission.program ?? "";
        const programName = programValue
          ? (programNameMap[programValue] ?? programValue)
          : "";

        // Get activity title
        const eventId = String(
          submission.event_id ?? submission.eventid ?? ""
        ).trim();
        let activityTitle = programName ?? "Unknown Activity";
        if (eventId) {
          const mappedTitle = activityMap.get(eventId);
          if (mappedTitle) {
            activityTitle = mappedTitle;
          }
        }

        // Get submission date
        const submittedAt =
          submission.submitted_at ??
          submission.created_on ??
          submission.created_at ??
          submission.submit_date ??
          submission.updated_on ??
          "";

        return {
          id: submissionId,
          event_id: eventId,
          status: submission.status ?? "pending",
          score: submission.score ?? null,
          submitted_at: submittedAt,
          created_at:
            submission.created_on ?? submission.created_at ?? submittedAt,
          school: {
            school_name:
              schoolProfile?.schoolname ??
              submission.schoolname ??
              "Unknown School",
            kc_no: schoolProfile?.kcno ?? submission.kcno ?? "N/A",
            kendra_name:
              schoolProfile?.kendra ?? submission.nearestkendra ?? "N/A",
          },
          activity: {
            title: activityTitle,
          },
          teacher: submission.teachername
            ? {
                name: submission.teachername,
              }
            : null,
        };
      })
      .filter((s): s is Submission => s !== null)
      .sort((a, b) => {
        // Sort by created_at descending (most recent first)
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });
  }, [submissionsData, activityMap, schoolProfileMap]);

  const loading = submissionsLoading;

  const filteredSubmissions = useMemo(() => {
    return submissions.filter(submission => {
      const matchesSearch =
        submission.school.school_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        submission.school.kc_no
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        submission.activity.title
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      if (statusFilter === "all") {
        return matchesSearch;
      }
      return matchesSearch && submission.status === statusFilter;
    });
  }, [submissions, searchTerm, statusFilter]);

  const {
    page: submissionPage,
    setPage: setSubmissionPage,
    totalPages: submissionTotalPages,
    paginatedItems: pagedSubmissions,
    totalItems: submissionFilteredTotal,
    pageSize: submissionPageSize,
    setPageSize: setSubmissionPageSize,
  } = useKarunaPagedList(filteredSubmissions, searchTerm, statusFilter);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: "bg-yellow-500", label: "Pending" },
      approved: { color: "bg-green-500", label: "Approved" },
      rejected: { color: "bg-red-500", label: "Rejected" },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] ?? statusConfig.pending;

    return (
      <Badge className={`${config.color} text-white`}>{config.label}</Badge>
    );
  };

  const getSubmissionStats = () => {
    return {
      total: submissions.length,
      pending: submissions.filter(s => s.status === "pending").length,
      approved: submissions.filter(s => s.status === "approved").length,
      rejected: submissions.filter(s => s.status === "rejected").length,
    };
  };

  const stats = getSubmissionStats();

  // Mutation to update submission status
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      submissionId,
      newStatus,
    }: {
      submissionId: string;
      newStatus: string;
    }) => {
      // Fetch the current submission data to preserve all existing fields
      let currentRecord: any;
      try {
        const response = await DynamicModuleService.getItemById(
          SUBMISSION_SCHEMA_NAME,
          submissionId
        );
        currentRecord = response?.data ?? response ?? {};
      } catch (fetchError) {
        console.warn(
          "⚠️ Could not fetch full record, using minimal data:",
          fetchError
        );
        currentRecord = {};
      }

      // Remove meta fields first
      const cleanedData = removeMetaFields(currentRecord);

      // If status is being changed to rejected or pending, explicitly delete score and evaluation fields
      // from cleanedData so they are not included in the update payload
      // This ensures score is cleared when changing from approved to rejected or pending
      if (newStatus === "rejected" || newStatus === "pending") {
        // Delete all possible variations of score and evaluation fields
        delete cleanedData.score;
        delete cleanedData.admin_comments;
        delete cleanedData.comments;
        delete cleanedData.review_comments;
        delete cleanedData.admincomments;
        delete cleanedData.reviewcomments;
        delete cleanedData.evaluation_score;
        delete cleanedData.evaluation_comments;
        // Also try common field name variations
        delete cleanedData.Score;
        delete cleanedData.Admin_Comments;
        delete cleanedData.Comments;
      }

      // Build update payload - merge existing data with status update
      const updatePayload: any = {
        ...cleanedData,
        status: newStatus,
      };

      // Add reviewed_at timestamp if status is being changed to approved/rejected
      if (newStatus === "approved" || newStatus === "rejected") {
        updatePayload.reviewed_at = new Date().toISOString();
        updatePayload.reviewed_on = new Date().toISOString();
      }

      // When changing to rejected or pending, explicitly set score and evaluation fields to null
      // to ensure backend clears them (backend should accept null to clear fields)
      if (newStatus === "rejected" || newStatus === "pending") {
        updatePayload.score = null;
        updatePayload.admin_comments = null;
        updatePayload.comments = null;
        updatePayload.review_comments = null;
        updatePayload.admincomments = null;
        updatePayload.reviewcomments = null;
        updatePayload.evaluation_score = null;
        updatePayload.evaluation_comments = null;
      }

      // Remove any undefined values, but preserve null values for fields we want to clear
      const fieldsToClear =
        newStatus === "rejected" || newStatus === "pending"
          ? [
              "score",
              "admin_comments",
              "comments",
              "review_comments",
              "admincomments",
              "reviewcomments",
              "evaluation_score",
              "evaluation_comments",
            ]
          : [];

      Object.keys(updatePayload).forEach(key => {
        if (updatePayload[key] === undefined) {
          delete updatePayload[key];
        } else if (
          updatePayload[key] === null &&
          !fieldsToClear.includes(key)
        ) {
          delete updatePayload[key];
        } else if (updatePayload[key] === "" && !fieldsToClear.includes(key)) {
          delete updatePayload[key];
        }
      });

      console.log(
        "💾 Updating submission with payload:",
        JSON.stringify(updatePayload, null, 2)
      );
      console.log("📋 Submission ID:", submissionId);
      console.log("📋 Schema Name:", SUBMISSION_SCHEMA_NAME);

      try {
        const response = await DynamicModuleService.updateItem(
          SUBMISSION_SCHEMA_NAME,
          submissionId,
          updatePayload
        );
        console.log("✅ Update successful:", response);
        return response;
      } catch (updateError: any) {
        console.error("❌ Update error details:", {
          status: updateError?.response?.status,
          statusText: updateError?.response?.statusText,
          data: updateError?.response?.data,
          message: updateError?.message,
          error: updateError,
        });
        throw updateError;
      }
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ["admin-submissions", SUBMISSION_SCHEMA_NAME],
      });
      toast({
        title: "Success",
        description: "Submission status updated successfully.",
      });
      setUpdatingStatusId(null);
    },
    onError: (error: any) => {
      console.error("Error updating submission status:", error);
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.response?.data?.detail ??
        error?.message ??
        "Failed to update submission status. Please try again.";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      setUpdatingStatusId(null);
    },
  });

  const handleStatusChange = async (
    submissionId: string,
    newStatus: string
  ) => {
    setUpdatingStatusId(submissionId);
    updateStatusMutation.mutate({ submissionId, newStatus });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="mb-2 text-3xl font-bold">Activity Submissions</h1>
          <p className="text-muted-foreground">
            Review and manage school activity submissions
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-muted-foreground">
                Total Submissions
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-yellow-600">
                {stats.pending}
              </div>
              <div className="text-sm text-muted-foreground">
                Pending Review
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-green-600">
                {stats.approved}
              </div>
              <div className="text-sm text-muted-foreground">Approved</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-red-600">
                {stats.rejected}
              </div>
              <div className="text-sm text-muted-foreground">Rejected</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative min-w-[300px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
            <Input
              placeholder="Search by school name, KC no, or event..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Submissions</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Submissions List */}
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredSubmissions.length > 0 ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <ListCardViewToggle
                viewMode={viewMode}
                onViewChange={setViewMode}
              />
            </div>

            <div
              className={
                viewMode === "card"
                  ? "grid gap-4 md:grid-cols-2 lg:grid-cols-3"
                  : "grid grid-cols-1 gap-3"
              }
            >
              {pagedSubmissions.map(submission => (
                <Card
                  key={submission.id}
                  className="transition-shadow hover:shadow-md"
                >
                  <CardContent className={viewMode === "list" ? "p-4" : "p-6"}>
                    <div
                      className={
                        viewMode === "list"
                          ? "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                          : "flex items-start justify-between gap-4"
                      }
                    >
                      <div className="min-w-0 flex-1 space-y-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <FileText className="h-5 w-5 shrink-0 text-primary" />
                          <h3
                            className={
                              viewMode === "list"
                                ? "line-clamp-1 text-base font-semibold"
                                : "text-lg font-semibold"
                            }
                          >
                            {submission.activity.title}
                          </h3>
                          {getStatusBadge(submission.status)}
                          <div className="flex items-center gap-2">
                            <Select
                              value={submission.status}
                              onValueChange={newStatus =>
                                handleStatusChange(submission.id, newStatus)
                              }
                              disabled={updatingStatusId === submission.id}
                            >
                              <SelectTrigger className="h-8 w-[140px]">
                                <SelectValue placeholder="Change status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="approved">
                                  Approved
                                </SelectItem>
                                <SelectItem value="rejected">
                                  Rejected
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            {updatingStatusId === submission.id && (
                              <Loader2 className="h-4 w-4 animate-spin text-primary" />
                            )}
                          </div>
                        </div>

                        <div className="grid gap-2 text-sm md:grid-cols-2">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <School className="h-4 w-4 shrink-0" />
                            <span className="truncate">
                              {submission.school.school_name} (
                              {submission.school.kc_no})
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="h-4 w-4 shrink-0" />
                            <span>
                              Submitted:{" "}
                              {submission.submitted_at
                                ? new Date(
                                    submission.submitted_at
                                  ).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })
                                : "N/A"}
                            </span>
                          </div>
                        </div>

                        {submission.score !== null && (
                          <div className="text-sm">
                            <span className="font-semibold">Score: </span>
                            <span className="font-bold text-primary">
                              {submission.score}/100
                            </span>
                          </div>
                        )}
                      </div>

                      <Button
                        onClick={() =>
                          navigate(
                            getLink(`/admin/submissions/${submission.id}`)
                          )
                        }
                        size="sm"
                        className="shrink-0"
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Review
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <KarunaPagination
              page={submissionPage}
              totalPages={submissionTotalPages}
              totalItems={submissionFilteredTotal}
              pageSize={submissionPageSize}
              onPageChange={setSubmissionPage}
              onPageSizeChange={setSubmissionPageSize}
            />
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                No Submissions Found
              </h3>
              <p className="text-muted-foreground">
                {(searchTerm ?? statusFilter !== "all")
                  ? "Try adjusting your filters"
                  : "No submissions have been received yet"}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSubmissions;
